// Client V1 imports
const airbrake = require('client/lib/airbrake')
const { API: ArticleAPI } = require('client/models/article')
// Common Client V2 imports
const articleModelActions = require('client-v2/common/models/article/actions')
const { FetchStateEnum } = require('client-v2/common/utils/enums')

// Action types

const COLLECTIONS_FAILED = 'ARTICLE_PROFILE_SLIDE_COLLECTIONS_FAILED'
const COLLECTIONS_LOADED = 'ARTICLE_PROFILE_SLIDE_COLLECTIONS_LOADED'
const COLLECTIONS_LOADING = 'ARTICLE_PROFILE_SLIDE_COLLECTIONS_LOADING'
const COMMENT_DELETED = 'ARTICLE_PROFILE_SLIDE_COMMENT_DELETED'
const COMMENTS_FAILED = 'ARTICLE_PROFILE_SLIDE_COMMENTS_FAILED'
const COMMENTS_LOADED = 'ARTICLE_PROFILE_SLIDE_COMMENTS_LOADED'
const COMMENTS_LOADING = 'ARTICLE_PROFILE_SLIDE_COMMENTS_LOADING'
const CURATOR_LOADED = 'ARTICLE_PROFILE_SLIDE_CURATOR_LOADED'
const INTERESTS_FAILED = 'ARTICLE_PROFILE_SLIDE_INTERESTS_FAILED'
const INTERESTS_LOADED = 'ARTICLE_PROFILE_SLIDE_INTERESTS_LOADED'
const INTERESTS_LOADING = 'ARTICLE_PROFILE_SLIDE_INTERESTS_LOADING'
const RELATED_FAILED = 'ARTICLE_PROFILE_SLIDE_RELATED_FAILED'
const RELATED_LOADED = 'ARTICLE_PROFILE_SLIDE_RELATED_LOADED'
const RELATED_LOADING = 'ARTICLE_PROFILE_SLIDE_RELATED_LOADING'
const SELECT_TAB = 'ARTICLE_PROFILE_SLIDE_SELECT_TAB'

// Action exports

module.exports = {
  // Types
  COLLECTIONS_FAILED,
  COLLECTIONS_LOADED,
  COLLECTIONS_LOADING,
  COMMENT_DELETED,
  COMMENTS_FAILED,
  COMMENTS_LOADED,
  COMMENTS_LOADING,
  CURATOR_LOADED,
  INTERESTS_FAILED,
  INTERESTS_LOADED,
  INTERESTS_LOADING,
  RELATED_FAILED,
  RELATED_LOADED,
  RELATED_LOADING,
  SELECT_TAB,
  // Actions
  addComment,
  deleteComment,
  slideDidBecomeVisible,
  loadComments,
  loadCollections,
  loadInterests,
  loadRelated,
  reportComment,
  selectTab
}

// Action dispatchers

function addComment({ article, commentText }) {
  const urlId = article['url_id']
  return function (dispatch) {
    if (!commentText.length) {
      return showAlert({
        heading: 'Comment text required.',
        subheading: 'You must enter some text to post a comment.'
      })
    }
    dispatch({
      type: COMMENTS_LOADING,
      payload: { urlId }
    })
    ArticleAPI.addComment(urlId, commentText)
    .then(({ comment }) => {
      dispatch({
        type: COMMENTS_LOADED,
        payload: { items: [comment], urlId }
      })
    })
    .catch(err => {
      airbrake.notify(err)
      dispatch({
        type: COMMENTS_LOADED,
        payload: { items: [], urlId }
      })
      showAlert({
        // TODO Better error messaging
        heading: 'Something went wrong.',
        subheading: 'Sorry, we could not add your comment at this time.'
      })
    })
  }
}

function deleteComment({ article, comment }) {
  const urlId = article['url_id']
  const commentId = comment.id
  return function (dispatch) {
    ArticleAPI.deleteComment(urlId, commentId)
    .then(() => {
      dispatch({
        type: COMMENT_DELETED,
        payload: { commentId, urlId }
      })
    })
    .catch(err => {
      airbrake.notify(err)
      showAlert({
        // TODO Better error messaging
        heading: 'Something went wrong.',
        subheading: 'Sorry, we could not delete your comment at this time.'
      })
    })
  }
}

function reportComment({ article, comment }) {
  const urlId = article['url_id']
  const commentId = comment.id
  return function (dispatch, getState) {
    const { user } = getState()
    ArticleAPI.reportComment(urlId, commentId, user.id)
    .then(() => {
      showAlert({
        heading: 'Comment Reported',
        subheading: 'Our team has been notified and will investigate shortly.'
      })
    })
    .catch(err => {
      airbrake.notify(err)
      showAlert({
        // TODO Better error messaging
        heading: 'Something went wrong.',
        subheading: 'Sorry, we could not process your report at this time.'
      })
    })
  }
}

function loadCollections({ article, nextUrl }) {
  const urlId = article['url_id']
  const options = nextUrl ? { nextUrl } : undefined
  return async function (dispatch) {
    dispatch({
      type: COLLECTIONS_LOADING,
      payload: { urlId }
    })
    try {
      const { curator, items, meta } =
        await ArticleAPI.fetchUrlMixHistoryV2(urlId, options)
      dispatch({
        type: COLLECTIONS_LOADED,
        payload: { items, meta, urlId }
      })
      if (curator) {
        dispatch({
          type: CURATOR_LOADED,
          payload: { ...curator, urlId }
        })
      }
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: COLLECTIONS_FAILED,
        payload: { urlId }
      })
    }
  }
}

function loadComments({ article }) {
  const urlId = article['url_id']
  return function (dispatch) {
    dispatch({
      type: COMMENTS_LOADING,
      payload: { urlId }
    })
    ArticleAPI.fetchComments(urlId)
    .then(({ comments: items }) => {
      dispatch({
        type: COMMENTS_LOADED,
        payload: { items, urlId }
      })
    })
    .catch(err => {
      airbrake.notify(err)
      dispatch({
        type: COMMENTS_FAILED,
        payload: { urlId }
      })
    })
  }
}

function loadInterests({ article }) {
  const urlId = article['url_id']
  return function (dispatch) {
    dispatch({
      type: INTERESTS_LOADING,
      payload: { urlId }
    })
    ArticleAPI.fetchInterests(article.hash)
    .then(({ interests: items }) => {
      dispatch({
        type: INTERESTS_LOADED,
        payload: { items, urlId }
      })
    })
    .catch(err => {
      airbrake.notify(err)
      dispatch({
        type: INTERESTS_FAILED,
        payload: { urlId }
      })
    })
  }
}

function loadRelated({ article, nextUrl }) {
  return function (dispatch) {
    const urlId = article['url_id']
    const options = { limit: 12, nextUrl }
    dispatch({
      type: RELATED_LOADING,
      payload: { urlId }
    })
    ArticleAPI.fetchRelatedArticles(urlId, options)
    .then(({ items, meta }) => {
      dispatch({
        type: RELATED_LOADED,
        payload: { items, meta, urlId }
      })
    })
    .catch(err => {
      airbrake.notify(err)
      dispatch({
        type: RELATED_FAILED,
        payload: { urlId }
      })
    })
  }
}

function slideDidBecomeVisible({ slide, pageContext = {} }) {
  const fetchOptions = { article: slide.article }
  return function (dispatch) {
    if (needsFetch(slide.articleState)) {
      dispatch(articleModelActions.fetchArticle(fetchOptions))
    }
    if (needsFetch(slide.collectionsState)) {
      dispatch(loadCollections(fetchOptions))
    }
    if (needsFetch(slide.commentsState)) {
      dispatch(loadComments(fetchOptions))
    }
    if (needsFetch(slide.interestsState)) {
      dispatch(loadInterests(fetchOptions))
    }
    if (!slide.relatedStream) {
      dispatch(loadRelated(fetchOptions))
    }
    dispatch(articleModelActions.trackCardView({
      article: slide.article,
      pageContext
    }))
  }
}

// Action creators

function selectTab({ article, selectedTab }) {
  const urlId = article['url_id']
  return {
    type: SELECT_TAB,
    payload: { selectedTab, urlId }
  }
}

// Internal helpers

function showAlert(options) {
  // Mitigate circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return clientV1Bridge.showAlertModal(options)
}

function needsFetch(fetchState) {
  return fetchState === FetchStateEnum.EMPTY
}
